<template>
    <v-container class="content-container">
        <v-row>
            <v-col>

                <!-- Page title -->
                <div class="page-heading">
                    Urejanje profila
                </div>

                <div v-if="appStore.PasswordChange.Status != IPasswordStatus.Undefined">
                    <!-- Alert -->
                    <div v-if="appStore.PasswordChange.Status == IPasswordStatus.Success"
                        class="alert alert--success mb-10">
                        <img src="@assets/images/general/icon-check-circle.svg" alt="Check">
                        {{ appStore.PasswordChange.DescriptionText }}
                    </div>

                    <!-- Alert -->
                    <div class="alert alert--error mb-10"
                        v-else-if="appStore.PasswordChange.Status == IPasswordStatus.Error">
                        <img src="@assets/images/general/icon-cancel.svg" alt="Cancel">
                        {{ appStore.PasswordChange.DescriptionText }}
                    </div>

                </div>

                <!-- Change password title -->
                <div class="section-heading">
                    Sprememba gesla
                </div>

                <v-row>
                    <v-col class="v-col-12 v-col-md-6">

                        <!-- Old password -->
                        <div class="mb-8">
                            <label class="form-label">
                                Staro geslo
                            </label>
                            <v-text-field type="password" variant="outlined"
                                v-model="appStore.PasswordChange.OldPassword"
                                :error-messages="v$.OldPassword.$errors[0] != null ? v$.OldPassword.$errors[0].$message : ''"
                                @focus="v$.OldPassword.$reset">
                            </v-text-field>
                        </div>

                        <!-- New password -->
                        <div class="mb-8">
                            <label class="form-label">
                                Novo geslo
                            </label>
                            <v-text-field type="password" variant="outlined" hint="Geslo mora biti dolgo najmanj 8 znakov in mora vsebovati vsaj
                                            eno veliko črko, eno malo črko, eno številko in en poseben znak."
                                persistent-hint v-model="v$.NewPassword.$model"
                                :error-messages="v$.NewPassword.$errors[0] != null ? v$.NewPassword.$errors[0].$message : ''"
                                @focus="v$.NewPassword.$reset">
                            </v-text-field>
                        </div>

                        <!-- Repeat new password -->
                        <div>
                            <label class="form-label">
                                Ponovno novo geslo
                            </label>
                            <v-text-field variant="outlined" v-model="v$.RepeatPassword.$model"
                                :error-messages="v$.RepeatPassword.$errors[0] != null ? v$.RepeatPassword.$errors[0].$message : ''"
                                @focus="v$.RepeatPassword.$reset" type="password">
                            </v-text-field>
                        </div>

                    </v-col>

                </v-row>

                <!-- Panel footer -->
                <div class="my-14">
                    <v-btn :disabled="appStore.User.NeedsToChangePassword" flat class="btn btn--default-fill mr-4"
                        @click="DeclinePasswordChange()">
                        {{ $t(`GeneralButtons.Cancel`) }}
                    </v-btn>
                    <v-btn flat class="btn btn--primary-fill" @click="SavePassword()">
                        {{ $t(`GeneralButtons.Save`) }}
                    </v-btn>
                </div>

            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import { useAppStore, IPasswordStatus } from "@/app/store/app/store";
import { computed, onMounted } from "vue";
import { useVuelidate } from "@vuelidate/core";
import errors from "@/app/global-errors";
import { helpers } from "@vuelidate/validators";

const appStore = useAppStore();

//nastavi Password status na undefined, da ne prikazuje alert message ter vrednosti na default
onMounted(() => {
    appStore.ChangePasswordClearValues();
    v$.value.$reset();
    appStore.OpenChangePassModal = false
});

async function SavePassword() {
    await validateForm().then(() => {
        v$.value.$reset();
        appStore.savePassword();
    });
}

function DeclinePasswordChange() {
    appStore.cancelSavePassword();
}

async function validateForm(): Promise<void> {
    v$.value.$touch();
    let formValid = await v$.value.$validate();
    return new Promise((resolve) => {
        if (formValid) {
            resolve();
        }
    });
};

const validationRules = computed(() => ({
    OldPassword: {
        required: errors.fieldRequired,
    },
    NewPassword: {
        required: errors.fieldRequired,
        complexPassword: helpers.withMessage("Geslo mora biti dolgo najmanj 8 znakov in mora vsebovati vsaj eno veliko črko, eno malo črko, eno številko in en poseben znak.", () => /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z\d\s])\S{8,}$/.test(appStore.PasswordChange.NewPassword)),
        matchesOld: helpers.withMessage("Novo geslo ne sme biti enako staremu geslu", () => appStore.PasswordChange.OldPassword != appStore.PasswordChange.NewPassword)
    },
    RepeatPassword: {
        required: errors.fieldRequired,
        matches: helpers.withMessage("Gesli se ne ujemata", () => appStore.PasswordChange.NewPassword == appStore.PasswordChange.RepeatPassword)
    }
}));

const v$: any = useVuelidate(validationRules, appStore.PasswordChange, { $autoDirty: true });
</script>